import { Component, OnInit, ViewChild, ChangeDetectorRef } from '@angular/core';
import {
  GridDataResult,
  PagerSettings,
  DataStateChangeEvent,
} from '@progress/kendo-angular-grid';
import {
  LoaderService,
  MessageService,
  ColumnOptions,
  GridRowType,
  DataGridComponent,
  GridState,
  GridStateCompositeFilterDescriptor,
  ValidationResultService,
  NamedEntity,
} from '@shared';
import { Category } from '@restaurant-item';
import {
  GridRowItemExtended,
  ItemExtend,
} from '../../models/item-extended.model';
import {
  CategoryService,
  RevenueCenterService,
  RevenueCenter,
} from '../../services';
import { itemProductTypes } from 'src/app/restaurant-item/restaurant-item/constants';
import { GlobalEventsService, ItemService } from '@shared-data';
import { SortDescriptor } from '@progress/kendo-data-query';

@Component({
  selector: 'app-category',
  templateUrl: './item.component.html',
  styleUrls: ['./item.component.scss'],
})
export class ItemComponent implements OnInit {
  items: GridDataResult = { data: [], total: 0 };
  pageSize = 8;
  state = new GridState(this.pageSize);
  columns: ColumnOptions[] = [];
  pagerSettings: PagerSettings = {
    buttonCount: 3,
    info: true,
    type: 'numeric',
    previousNext: true,
  };
  categories: Category[] = [];
  revenueCenters: RevenueCenter[] = [];
  selectedCategory: any;
  isUnassign: boolean = true;
  showItemUpdatePopup: boolean = false;
  showLegacyCombo: boolean = false;
  productTypes = itemProductTypes;
  fontSize: number = 0.875;
  isEditMode = false;
  cache: Map<string, GridRowItemExtended> = new Map<
    string,
    GridRowItemExtended
  >();

  @ViewChild(DataGridComponent)
  grid!: DataGridComponent;

  itemToUploadDocuments: GridRowItemExtended | null = null;
  itemToRemoveDocument: { itemId: number; documentPath: string } | null = null;
  hasOverrideChanges: boolean = false;

  // Popup settings for dropdowns
  dropdownPopupSettings = { width: 200 };

  importUrl: string = '';

  initialSort: SortDescriptor[] = [
    { field: 'sortOrder', dir: 'asc' },
    { field: 'itemName', dir: 'asc' },
  ];

  machineTypes: NamedEntity[] = [];

  selectedConcept: NamedEntity | null = null;

  constructor(
    private itemService: ItemService,
    private messageService: MessageService,
    private loaderService: LoaderService,
    private categoryService: CategoryService,
    private revenueCenterService: RevenueCenterService,
    private validationResultService: ValidationResultService,
    private cdr: ChangeDetectorRef,
    private globalEventsService: GlobalEventsService,
  ) {
    this.columns = new GridRowItemExtended().getColumns();
    this.importUrl = this.itemService.getImportUrl();
  }

  ngOnInit(): void {
    this.subscribeToConceptChanges();
  }

  subscribeToConceptChanges() {
    this.globalEventsService.selectedConcept$.subscribe((concept) => {
        this.selectedCategory = null;
        this.selectedConcept = concept;
        this.setDefaultState();
    });
  }

  fetchMachineTypes() {
    this.itemService.getMachineDependencies({} as GridState).subscribe({
      next: (result) => {
        this.machineTypes = result.data;
      },
      error: (error) => {
        var validationError = error?.error;
        if (validationError) {
          this.validationResultService.show(validationError);
        }
        this.messageService.error('Failed to load machine types');
      },
    });
  }

  fetchDropDownCategories(filter?: string) {
    const state = new GridState(100);
    state.sort = [{ field: 'sortOrder', dir: 'asc' }];
    state.filter = state.filter?.and({
      field: 'conceptKey',
      operator: 'eq',
      value: this.selectedConcept?.id,
    });

    this.categoryService.getAllCategories(state).subscribe({
      next: (result) => {
        // Convert id to number if it's a string
        this.categories = result.data.map((cat: any) => ({
          ...cat,
          id: typeof cat.id === 'string' ? parseInt(cat.id) : cat.id,
        })) as Category[];
        if (filter) {
          this.categories = this.categories.filter((cat) =>
            cat.name.toLowerCase().includes(filter),
          );
        }
      },
      error: () => {
        this.messageService.error('Failed to load categories');
      },
    });
  }

  fetchRevenueCenters() {
    const state = new GridState(100);
    this.revenueCenterService.getAllRevenueCenters(state).subscribe({
      next: (result) => {
        this.revenueCenters = result.data;
        this.cdr.detectChanges();
      },
      error: () => {
        this.messageService.error('Failed to load revenue centers');
      },
    });
  }

  onDataStateChanged(event: DataStateChangeEvent): void {
    this.state = event as GridState;
    this.fetchItems(this.state);
  }

  setDefaultState(): void {
    this.state = new GridState(this.pageSize, this.initialSort);
    this.state.filter = this.state.filter?.and(
      { field: 'conceptKey', operator: 'eq', value: this.selectedConcept?.id }
    )

    this.fetchItems(this.state);
    this.fetchDropDownCategories();
    this.fetchMachineTypes();
    this.fetchRevenueCenters();
    this.isEditMode = false;
  }

  fetchItems(state: GridState): void {
    this.loaderService.show();

    if (state.skip !== undefined) this.state.skip = state.skip;
    if (state.take !== undefined) this.state.take = state.take;
    if (state.sort?.length) {
      this.state.sort = state.sort;
    } else {
      this.state.sort = this.initialSort;
    }

    this.state.filter = new GridStateCompositeFilterDescriptor(
      GridState.removeFilterByFields(
        new GridStateCompositeFilterDescriptor(this.state.filter),
        'categoryId',
      ),
    );

    this.state.filter = new GridStateCompositeFilterDescriptor(
      state.filter,
    ).and({
      field: 'categoryId',
      operator: this.selectedCategory?.id
        ? 'eq'
        : this.isUnassign
          ? 'eq'
          : 'neq',
      value: this.selectedCategory?.id ?? null,
    });
    this.itemService.getAllItems(this.state, this.showLegacyCombo).subscribe({
      next: (result: GridDataResult) => {
        this.items = result;
        this.items.data = this.items.data.map((item) => {
          // Ensure revenue center exists in the array if item has a revenueCenterId
          if (item.revenueCenterId != null && item.revenueCenterDescription) {
            const exists = this.revenueCenters.some(
              (rc) => rc.id === item.revenueCenterId,
            );
            if (!exists) {
              this.revenueCenters.push({
                id: item.revenueCenterId,
                name: item.revenueCenterDescription,
              });
            }
          }
          const itemRow = new GridRowItemExtended(item);
          return itemRow;
        });
        this.restoreCachedChanges();
        this.loaderService.hide();
      },
      error: () => {
        this.messageService.error('Failed to load items');
        this.loaderService.hide();
      },
    });
  }

  onTypeChange(event: any) {
    if (!this.isUnassign) {
      this.setDefaultState();
    }
  }

  onUnassignedToggle(selected: boolean): void {
    this.isUnassign = selected;
    if (this.isUnassign) {
      this.selectedCategory = null;
    }
    this.setDefaultState();
  }

  closeUpdatePopup() {
    this.showItemUpdatePopup = false;
    this.fetchItems(this.state);
  }

  handleFilter(value: string): void {
    const filter = value?.toLowerCase() || '';
    this.fetchDropDownCategories(filter);
  }

  legacyFilter(value: boolean) {
    this.showLegacyCombo = value;
    this.setDefaultState();
  }

  confirmItemDetailsUpdate(): void {
    this.showItemUpdatePopup = false;
    this.itemService
      .batchUpdateItems(Array.from(this.cache.values()))
      .subscribe({
        next: (res) => {
          this.messageService.success('Items updated successfully.');
          this.cache.clear();
          this.clearDirtyRows();
          this.isEditMode = false;
          this.fetchItems(this.state);
        },
        error: (err) => {
          this.messageService.error('Failed to Update the Items.');
          this.loaderService.hide();
        },
      });
  }

  onSaveItem() {
    // Check if any items have disableParUpdates field changed
    this.hasOverrideChanges = Array.from(this.cache.values()).some((item) =>
      item.dirty.has('disableParUpdates'),
    );

    this.showItemUpdatePopup = true;
  }

  setEditMode(): void {
    this.isEditMode = true;
  }

  onValueChange(
    dataItem: { [key: string]: any } & GridRowType,
    columnField: string,
  ): void {
    this.grid.onValueChange(dataItem, columnField);
  }

  cacheChanges() {
    this.items.data?.forEach((row) => {
      const editableColumns = this.columns.filter((column) => column.editable);
      const isDirty = editableColumns.some((column) =>
        row.dirty.get(column.field),
      );
      if (isDirty) {
        this.cache.set(row.id, row);
      } else {
        this.cache.delete(row.id);
      }
      row.errors.clear();
    });
  }

  restoreCachedChanges(): void {
    this.items.data.forEach((row) => {
      const cachedRow = this.cache.get(row.id);

      if (cachedRow) {
        const editableColumns = this.columns.filter(
          (column) => column.editable,
        );
        editableColumns.forEach((column) => {
          row[column.field as keyof ItemExtend] =
            cachedRow[column.field as keyof ItemExtend];
        });

        row.dirty = cachedRow.dirty;
      }
    });
  }

  clearDirtyRows() {
    this.items.data.forEach((row) => row.dirty.clear());
  }

  onCancelClick() {
    this.clearDirtyRows();
    this.setDefaultState();
  }

  downloadDocument(
    newItem: { itemName: string; parItemId: string },
    documentPath: string,
  ) {
    this.itemService.downloadDocument(documentPath).subscribe({
      next: async (response: { url: string }) => {
        const downloadLink = document.createElement('a');
        downloadLink.target = '_blank';
        downloadLink.href = response.url;
        downloadLink.download = `${newItem.itemName}_${newItem.parItemId}.pdf`; // Suggest a filename
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
      },
      error: (error) => {
        this.messageService.error('Error downloading document');
      },
    });
  }

  onUploadComplete(event: any) {
    this.itemToUploadDocuments = null;
    this.messageService.success('Documents uploaded successfully.');
    this.fetchItems(this.state);
  }

  confirmDocumentRemoval() {
    if (this.itemToRemoveDocument == null) return;
    this.itemService
      .removeDocument(
        this.itemToRemoveDocument?.itemId,
        this.itemToRemoveDocument?.documentPath,
      )
      .subscribe({
        next: () => {
          this.messageService.success('Document removed successfully.');
          this.fetchItems(this.state);
          this.itemToRemoveDocument = null;
        },
        error: (error) => {
          const validationError = error?.error;
          if (validationError) {
            this.validationResultService.show(validationError);
          }
          this.messageService.error('Error removing document');
          this.itemToRemoveDocument = null;
        },
      });
  }

  /**
   * Get tooltip text for PAR protection icon
   */
  getParProtectionTooltip(item: GridRowItemExtended): string {
    if (item.disableParUpdates) {
      return 'Custom values protected from PAR updates\nProtected fields: Name, Description, PLU, BKPN, Type, Revenue Center';
    }
    return 'Values will be updated by PAR imports';
  }
}
